/**
 * Simplified hook for managing AI chat conversations with Server-Sent Events streaming.
 * @remarks
 * This hook provides a clean interface for AI chat functionality with:
 * - Automatic message management via Apollo reactive variables
 * - Type-safe SSE event handling using discriminated unions
 * - A bounded wait for the backend, enforced client-side (see below)
 * - Direct integration with useSSE for connection management
 * - Operation hooks for immutable state updates
 *
 * Migration from Zustand:
 * - Replaced Zustand chatStore with Apollo reactive variables
 * - Uses operation hooks (useMessageOperations, useConnectionOperations, etc.)
 * - User ID comes from currentUser (no need for separate useChatUserId hook)
 * - Maintains same public API for backward compatibility with consumers
 *
 * Key simplifications from previous version:
 * - Removed complex inline message parsing (uses SSE event types)
 * - Removed interrupted query retry mechanism
 * - Reduced complexity while maintaining all functionality
 *
 * ## Loading state is no longer the backend's sole responsibility (SE-7078)
 *
 * This hook previously documented "trust in backend completion events (no
 * manual timeouts)" and "removed manual timeout management" as design choices.
 * **They are reversed.** `isLoadingVar` was cleared only by a `complete` /
 * `end` / `error` frame or the EventSource's `close` / `onError`, which meant a
 * stream that ended without a terminal frame left the spinner running forever
 * — the reported bug.
 *
 * The timers do not live here. `useSSE` owns them, because it is the only layer
 * that sees every frame (this hook ignores `citations` and unnamed frames, and
 * treating those as silence would cut healthy streams short). It reports an
 * expiry through `onTimeout`, which this hook turns into cleared loading state
 * and a user-visible, retry-able message.
 * @example
 * ```tsx
 * const { messages, sendMessage, isLoading } = useChat();
 *
 * const handleSend = async () => {
 *   await sendMessage("What is the weather?", false);
 * };
 * ```
 */

import { useReactiveVar } from "@apollo/client";
import { useCallback, useLayoutEffect, useMemo, useRef } from "react";
import { IMessage } from "react-native-gifted-chat";

import { DEFAULT_ORGANIZATION_ID } from "../constants";

import { QueryRequest } from "../types";
import { resolveDispatchSessionId } from "../utils/authUtils";
import {
  StreamTimeoutReason,
  streamTimeoutMessage,
} from "../utils/streamTimeouts";
import {
  isCompleteEvent,
  isEndEvent,
  isErrorEvent,
  isStartEvent,
  isStatusEvent,
  isTokenEvent,
  SSEEvent,
} from "../types/sseEvents";
import { useConnectionOperations } from "./useConnectionOperations";
import { useMessageOperations } from "./useMessageOperations";
import { useSSE } from "./useSSE";
import { useStatusOperations } from "./useStatusOperations";
import { useThreadListSync } from "./useThreadListSync";
import type { IAgentProgress } from "@/stores/knowledgeGraph";

import { useAuth, useCurrentUser } from "@/features/auth";
import { getFreshAccessToken } from "@/providers/accessTokenSource";
import {
  connectionStatusVar,
  errorVar,
  isLoadingVar,
  messagesVar,
  organizationIdVar,
  playerIdVar,
  selectedThreadIdVar,
  sessionIdVar,
} from "@/stores/knowledgeGraph";

/**
 * Return type for useChat hook containing chat state and operations.
 */
interface UseChatReturn {
  /** Array of chat messages in the conversation */
  messages: IMessage[];
  /** Whether a query is currently being processed */
  isLoading: boolean;
  /** Error message if query failed, null otherwise */
  error: string | null;
  /** Function to send a new message to the AI */
  sendMessage: (text: string, researchMode?: boolean) => Promise<void>;
  /** Function to clear all messages from chat */
  clearChat: () => void;
  /** Function to load historical messages (no-op in current version) */
  loadMoreMessages: () => Promise<void>;
  /** Function to refresh chat state and clear errors */
  refreshChat: () => void;
  /** Whether sending a message is currently allowed */
  canSendMessage: boolean;
  /** Function to check if message can be sent with given text */
  canSendMessageWithText: (text: string) => boolean;
}

/**
 * Maps a status frame's stage to the step's progress status.
 * @param stage - The frame's stage
 * @returns `complete` or `error` for those stages, otherwise `running`
 */
const progressStatusOf = (stage: string): IAgentProgress["status"] => {
  if (stage === "complete") return "complete";
  if (stage === "error") return "error";
  return "running";
};

/**
 * Whether a status frame should show the progress display.
 * @param stage - The frame's stage
 * @returns True before this turn's answer has started, or for an error
 * @remarks
 * SE-8646: v2 also sends status frames after the answer has started — a
 * closing `agent complete` behind the last token. The progress display is
 * gated on the current status, so setting it then re-mounted the step rows
 * for a moment after they had given way to the answer. The answer has
 * started when the last message is the assistant's; a new send ends that by
 * appending the question, so the rule resets per turn.
 */
const statusShowsProgress = (stage: string): boolean =>
  stage === "error" || messagesVar().at(-1)?.user._id !== "ai";

/**
 * Custom hook for managing AI chat conversations with Server-Sent Events streaming.
 * @returns Chat state and operations for managing AI conversations
 * @remarks
 * Handles message sending, receiving, and streaming responses from the AI backend.
 * Manages connection state and error handling. Bounds the wait with the
 * client-side stream watchdogs in `useSSE` rather than relying on the backend
 * to always send a terminal frame (SE-7078). Uses Apollo reactive variables for
 * state management instead of Zustand store.
 */
export const useChat = (): UseChatReturn => {
  // Read reactive variables
  const messages = useReactiveVar(messagesVar);
  const isLoading = useReactiveVar(isLoadingVar);
  const error = useReactiveVar(errorVar);
  const sessionId = useReactiveVar(sessionIdVar);
  const connectionStatus = useReactiveVar(connectionStatusVar);
  const organizationId = useReactiveVar(organizationIdVar);
  const playerId = useReactiveVar(playerIdVar);

  const auth = useAuth();
  const currentUser = useCurrentUser();

  // Get operation hooks
  const { addMessage, clearMessages } = useMessageOperations();
  const { clearError } = useConnectionOperations();
  const { updateCurrentStatus, addAgentProgress, clearAgentProgress } =
    useStatusOperations();
  const { recordCompletedTurn } = useThreadListSync();

  // Refs to store teardown functions from useSSE. Two of them because the
  // choice matters: `disconnect` reports "disconnected", `abort` reports
  // nothing and so preserves a status the transport already set (SE-7078).
  const disconnectRef = useRef<(() => void) | null>(null);
  const abortRef = useRef<(() => void) | null>(null);

  // SE-8865: the id ask-gemini lists this question's thread under, from the
  // stream's `start` frame. It is not the session id the client sent (the
  // server sanitizes that), so it is the only id a Conversations row can be
  // keyed by. Cleared on every send so one question's id never outlives it.
  const threadIdRef = useRef<string | null>(null);

  /**
   * Handle SSE message events.
   * @param event - SSE event to process
   */
  const handleMessage = useCallback(
    (event: SSEEvent) => {
      if (isStartEvent(event)) {
        threadIdRef.current = event.sessionId || null;
        return;
      }

      // Handle completion events - clear loading and status (SE-3546)
      if (isCompleteEvent(event) || isEndEvent(event)) {
        isLoadingVar(false);
        updateCurrentStatus(null); // Clear status to hide progress bar
        disconnectRef.current?.(); // Close connection to prevent reconnection loop
        recordCompletedTurn(threadIdRef.current); // SE-8865: list the finished chat
        return;
      }

      // Handle error events
      if (isErrorEvent(event)) {
        errorVar(event.content || "An error occurred");
        isLoadingVar(false);
        updateCurrentStatus(null); // Clear status on error too
        disconnectRef.current?.(); // Close connection on error
        return;
      }

      // Handle token streaming
      if (isTokenEvent(event)) {
        const content = event.content;
        if (!content) {
          return;
        }

        // Check if last message is from AI
        const currentMessages = messagesVar();
        const lastMessage = currentMessages[currentMessages.length - 1];
        const isLastMessageFromAI = lastMessage?.user._id === "ai";

        if (isLastMessageFromAI) {
          // Append to existing message
          const updatedMessage = {
            ...lastMessage,
            text: (lastMessage.text || "") + content,
          };
          const newMessages = [...currentMessages.slice(0, -1), updatedMessage];
          messagesVar(newMessages);
        } else {
          // Create new AI message - clear status to hide progress bar (SE-3583)
          updateCurrentStatus(null);

          const message: IMessage = {
            _id: Date.now().toString(),
            text: content,
            createdAt: new Date(),
            user: {
              _id: "ai",
              name: "Assistant",
            },
          };
          const newMessages = [...currentMessages, message];
          messagesVar(newMessages);
        }
        return;
      }

      // Handle status events - use actual agent data from SSE (SE-3546)
      if (isStatusEvent(event)) {
        const agent = event.agent || "ai";
        const stage = event.stage || "progress";
        const message = event.message || event.content || "";
        const timestamp = event.timestamp || Date.now();

        // The step is recorded below either way; only the progress display
        // is held back once the answer has started (SE-8646).
        if (statusShowsProgress(stage)) {
          updateCurrentStatus({
            agent,
            message,
            stage,
            timestamp,
          });
        }

        // Add to agent progress tracking for detailed status display
        const agentProgress: IAgentProgress = {
          agent,
          status: progressStatusOf(stage),
          message,
          timestamp,
        };
        addAgentProgress(agentProgress);
      }

      // Ignore other event types (citations, etc.)
    },
    [updateCurrentStatus, addAgentProgress, recordCompletedTurn]
  );

  /**
   * Handle SSE connection errors.
   * @param error - Error that occurred
   * @remarks
   * Also closes the stream. The transport reports an error without closing, so
   * leaving it open kept a dead connection (and its watchdogs) alive behind an
   * error the user had already been shown (SE-7078).
   *
   * Closed with `abort`, not `disconnect`: the transport's error listener has
   * already set connection status to `error`, and `disconnect` would overwrite
   * it with `disconnected` — for which `ConnectionStatusView` renders nothing,
   * hiding the error strip on the one path it exists for.
   */
  const handleError = useCallback(
    (error: Error) => {
      errorVar(error.message);
      isLoadingVar(false);
      updateCurrentStatus(null);
      abortRef.current?.();
    },
    [updateCurrentStatus]
  );

  /**
   * Handle a client-side stream watchdog expiring (SE-7078).
   * @param reason - Which budget was exceeded
   * @remarks
   * Guarded on `isLoadingVar` because a watchdog can only ever be *late*, never
   * wrong: if the answer already landed and something failed to cancel the
   * timer, there is nothing hanging and replacing the finished state with an
   * error would be a fabricated failure. `useSSE` has already torn the stream
   * down by the time this runs.
   *
   * The partial-answer check is separate from that guard. A stream can die
   * after some tokens have rendered, which leaves the user looking at real
   * output while loading is still legitimately true — telling them the
   * assistant never responded would contradict what is on their screen. It is
   * also the safety net for a terminator this client failed to recognise:
   * `useSSE` now honours a terminal event NAME even when its payload does not
   * parse, which closes the known instance of that, but the copy should still
   * degrade rather than report a total failure over good output.
   */
  const handleTimeout = useCallback(
    (reason: StreamTimeoutReason) => {
      if (!isLoadingVar()) {
        return;
      }

      const currentMessages = messagesVar();
      const lastMessage = currentMessages[currentMessages.length - 1];
      const hasPartialAnswer = Boolean(
        lastMessage?.user._id === "ai" && lastMessage.text
      );

      isLoadingVar(false);
      updateCurrentStatus(null);
      errorVar(streamTimeoutMessage(reason, hasPartialAnswer));
    },
    [updateCurrentStatus]
  );

  // Initialize SSE connection
  const {
    sendMessage: sseClientSendMessage,
    disconnect,
    abort,
  } = useSSE({
    onMessage: handleMessage,
    onError: handleError,
    onTimeout: handleTimeout,
    onOpen: () => {
      // Don't set loading to false here - it will be cleared by completion/error events
    },
    onClose: () => {
      isLoadingVar(false);
    },
  });

  /**
   * Check if sending messages is currently allowed.
   */
  const canSendMessage = useMemo(
    () =>
      connectionStatus !== "connecting" &&
      !isLoading &&
      Boolean(auth?.data?.accessToken) &&
      // Ask Gemini is authenticated-only and must query the user's real org.
      // Hold sending until currentUser (and its organizationId) has resolved so
      // a request is never built while the profile is still loading — otherwise
      // it falls back to the default-org env value, an org the user does not
      // belong to, and the agent returns 401 "Invalid session" (SE-5426/SE-5427).
      Boolean(currentUser?.organizationId),
    [
      connectionStatus,
      isLoading,
      auth?.data?.accessToken,
      currentUser?.organizationId,
    ]
  );

  /**
   * Send a message to the AI.
   * @param text - Message text to send
   * @param researchMode - Whether to enable research mode for deep analysis
   */
  const sendMessage = useCallback(
    async (text: string, researchMode?: boolean) => {
      if (!canSendMessage || !text.trim()) {
        return;
      }

      // Block Default organization users from sending messages
      if (currentUser?.organizationId === DEFAULT_ORGANIZATION_ID) {
        const errorMessage: IMessage = {
          _id: `blocked-${Date.now()}`,
          text: "AskGemini is not available for users in the Default organization. Please contact your administrator to be added to a proper organization.",
          createdAt: new Date(),
          user: {
            _id: "system",
            name: "System",
          },
          system: true,
        };
        addMessage(errorMessage);
        return;
      }

      // Add user message immediately
      const userMessage: IMessage = {
        _id: Date.now().toString(),
        text,
        createdAt: new Date(),
        user: {
          _id: "user",
          name: "You",
        },
        ...(researchMode && {
          metadata: { researchMode: true },
        }),
      };

      addMessage(userMessage);
      threadIdRef.current = null;
      isLoadingVar(true);
      errorVar(null);
      clearAgentProgress(); // Reset agent progress for new query (SE-3546)

      try {
        // Prefer the authenticated user's real org (canSendMessage already
        // gates on it) so a not-yet-updated organizationIdVar can never
        // reintroduce a stale/foreign org → agent 401 (SE-5426/SE-5427).
        const resolvedOrg = currentUser?.organizationId || organizationId || "";
        const selectedThreadId = selectedThreadIdVar();

        // SE-5451: the sessionId's org prefix must also carry the resolved org.
        // A cold-load session (`#fallbackUser#uuid`, minted before `me`
        // resolved) would otherwise be dispatched verbatim and rejected as
        // "Invalid session" even though organizationId is correct. Read reactive
        // vars directly to avoid stale closure values (e.g. after createNewThread).
        const effectiveSessionId = resolveDispatchSessionId({
          selectedThreadId,
          storedSessionId: sessionIdVar() || sessionId,
          organizationId: resolvedOrg,
          userId: currentUser?.id || "",
        });

        // Persist a regenerated new-chat session so the rest of the conversation
        // reuses the same (now org-correct) id.
        if (!selectedThreadId && effectiveSessionId !== sessionIdVar()) {
          sessionIdVar(effectiveSessionId);
        }

        // SE-8687: read the token now, refreshing it if it has expired. The
        // render-time `auth` value is whatever the last GraphQL request left
        // behind, which an hour of chatting alone lets expire.
        const accessKey = await getFreshAccessToken(auth?.data?.accessToken);

        const request: QueryRequest = {
          question: text.trim(),
          sessionId: effectiveSessionId,
          tenantId: resolvedOrg,
          organizationId: resolvedOrg,
          userId: currentUser?.id || "",
          isStreaming: true,
          usePlayerUuids: true,
          linkFormat: "uuid",
          playerIdFormat: "uuid",
          ...(playerId && { playerId }),
          ...(researchMode !== undefined && {
            webSearchRequested: researchMode,
          }),
          ...(accessKey && { accessKey }),
        };

        // Send via SSE client
        sseClientSendMessage(request);
      } catch (error) {
        errorVar(
          error instanceof Error ? error.message : "Failed to send message"
        );
        isLoadingVar(false);

        // Add error message to chat
        const errorMessage: IMessage = {
          _id: `error-${Date.now()}`,
          text: "Sorry, I encountered an error while processing your request. Please try again.",
          createdAt: new Date(),
          user: {
            _id: "ai",
            name: "Assistant",
          },
          system: true,
        };

        addMessage(errorMessage);
      }
    },
    [
      canSendMessage,
      currentUser,
      addMessage,
      sseClientSendMessage,
      auth,
      sessionId,
      organizationId,
      playerId,
      clearAgentProgress,
    ]
  );

  /**
   * Clear all chat messages and errors.
   */
  const clearChat = useCallback(() => {
    clearMessages();
    clearError();
  }, [clearMessages, clearError]);

  /**
   * Load more messages from history (no-op in current version).
   */
  const loadMoreMessages = useCallback(async () => {
    // Future implementation for message history loading
  }, []);

  /**
   * Refresh chat state and clear errors.
   */
  const refreshChat = useCallback(() => {
    clearError();
  }, [clearError]);

  /**
   * Check if message can be sent with the given text.
   * @param text - Text to check
   * @returns True if message can be sent
   */
  const canSendMessageWithText = useCallback(
    (text: string) => canSendMessage && text.trim().length > 0,
    [canSendMessage]
  );

  // Store the teardown functions in refs so handleMessage / handleError can
  // reach them. Must use useLayoutEffect to sync ref values before any
  // paint/useEffect callbacks
  useLayoutEffect(() => {
    disconnectRef.current = disconnect;
    abortRef.current = abort;
  }, [disconnect, abort]);

  return {
    messages,
    isLoading,
    error,
    sendMessage,
    clearChat,
    loadMoreMessages,
    refreshChat,
    canSendMessage,
    canSendMessageWithText,
  };
};
